/**
 * ClaimProfile.jsx — Parcours "Revendiquer cette fiche" pour les avocats
 * non référencés issus de l'annuaire officiel CNB.
 *
 * Différent de /claim/:token qui repose sur une invitation admin préalable :
 * ici l'avocat peut initier LUI-MÊME la revendication depuis /reseau-specialistes
 * sans invitation préalable. La demande passe en revue admin (`lawyer_claims`
 * status=pending_review) avant rattachement définitif à un compte.
 *
 * Conformité mission :
 * - La revendication seule n'active PAS les fonctionnalités premium (abonnement
 *   indépendant, présenté clairement à l'étape 3).
 * - CONFILEX ne certifie pas les compétences du seul fait de la revendication.
 * - La fiche reste marquée "issue de données publiques CNB" tant que la
 *   revendication n'est pas validée par l'équipe.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, Mail, IdCard, Send, Check, Loader2, ArrowLeft,
  Building2, MapPin, Info, ArrowRight,
} from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function ClaimProfile() {
  const { token } = useParams();
  const [lawyer, setLawyer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // Formulaire — champs strictement nécessaires (mission §7 minimisation)
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    bar_card_number: "",
    notes: "",
  });
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await apiClient.get(`/lawyers-directory/${token}`);
        if (!mounted) return;
        setLawyer(res.data);
        // Pré-remplit le nom si visible (pour les cabinets non-anonymisés)
        if (res.data?.fullName) {
          setForm((s) => ({ ...s, full_name: res.data.fullName }));
        }
      } catch (e) {
        setError(formatApiError(e?.response?.data?.detail) || "Fiche introuvable.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const isValid =
    form.full_name.trim().length >= 3 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
    form.bar_card_number.trim().length >= 3 &&
    consent;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) {
      toast.error("Veuillez renseigner votre nom, votre email professionnel, votre numéro d'inscription au Barreau et accepter les conditions.");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post(`/lawyers-directory/${token}/claim-request`, form);
      setSubmitted(true);
      toast.success("Demande de revendication envoyée. L'équipe CONFILEX vous contactera sous 48h.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Erreur lors de l'envoi.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5EE]">
      <Seo
        title="Revendiquer ma fiche — CONFILEX"
        description="Vous êtes cet avocat ? Revendiquez votre fiche pour en devenir le gestionnaire officiel sur CONFILEX."
      />
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to="/reseau-specialistes"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#0B3A2A] mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Retour à l'annuaire
        </Link>

        {loading ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-sm text-red-700">
            {error}
          </div>
        ) : submitted ? (
          <SuccessCard email={form.email} lawyer={lawyer} />
        ) : (
          <>
            {/* Header : fiche à revendiquer */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-[#0B3A2A]/10 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-5 h-5 text-[#0B3A2A]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-medium mb-1">
                    Revendication de fiche
                  </p>
                  <h1 className="font-serif text-2xl text-[#0B3A2A]">
                    {lawyer?.fullName || `Maître au barreau de ${lawyer?.barreau}`}
                  </h1>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-600">
                    {lawyer?.barreau && (
                      <span className="inline-flex items-center gap-1">
                        <Building2 className="w-3 h-3" /> Barreau de {lawyer.barreau}
                      </span>
                    )}
                    {lawyer?.city && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {lawyer.city}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500 leading-relaxed">
                Cette fiche a été créée à partir de l'annuaire officiel des avocats
                publié par le Conseil national des barreaux sur data.gouv.fr
                (Licence Ouverte Etalab 2.0). En revendiquant cette fiche, vous en
                devenez le <strong>gestionnaire officiel</strong> et pouvez la
                compléter avec vos informations professionnelles.
              </div>
            </div>

            {/* Formulaire */}
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
              <div>
                <Label htmlFor="full_name" className="text-sm">
                  Votre nom complet <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="full_name"
                  data-testid="claim-full-name"
                  value={form.full_name}
                  onChange={(e) => set("full_name", e.target.value)}
                  placeholder="Maître Prénom NOM"
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-sm">
                  Email professionnel <span className="text-red-500">*</span>
                </Label>
                <div className="mt-1 relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="email"
                    data-testid="claim-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="prenom.nom@cabinet.fr"
                    required
                    className="pl-9"
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  L'email doit provenir de votre cabinet ou de votre nom de domaine professionnel.
                </p>
              </div>

              <div>
                <Label htmlFor="bar_card_number" className="text-sm">
                  Numéro d'inscription au Barreau <span className="text-red-500">*</span>
                </Label>
                <div className="mt-1 relative">
                  <IdCard className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="bar_card_number"
                    data-testid="claim-bar-card"
                    value={form.bar_card_number}
                    onChange={(e) => set("bar_card_number", e.target.value)}
                    placeholder="ex : 12345 (Barreau de Paris)"
                    required
                    className="pl-9"
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  Utilisé uniquement pour vérifier votre inscription à l'Ordre.
                </p>
              </div>

              <div>
                <Label htmlFor="notes" className="text-sm">
                  Message complémentaire <span className="text-slate-400">(optionnel)</span>
                </Label>
                <Textarea
                  id="notes"
                  data-testid="claim-notes"
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  placeholder="Précisez une correction à apporter ou toute information utile."
                  rows={3}
                  className="mt-1"
                />
              </div>

              {/* Info : abonnement indépendant */}
              <div className="rounded-lg bg-[#F5EEDC] border border-[#C9A961]/30 p-3 flex gap-2 text-xs text-[#5C4D2A]">
                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <strong>À noter :</strong> La revendication vous permet de devenir
                  administrateur de votre fiche gratuitement. Les fonctionnalités
                  premium (salon complet, articles, vidéo, mise en avant éditoriale)
                  restent liées à un abonnement CONFILEX distinct, souscrit
                  ultérieurement à votre discrétion.
                </div>
              </div>

              {/* Consentement */}
              <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="claim-consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  Je certifie que les informations transmises sont exactes et que je
                  suis bien l'avocat référencé sur cette fiche. J'accepte que CONFILEX
                  procède à une vérification auprès de l'Ordre.{" "}
                  <Link to="/legal/definitions" className="text-[#0B3A2A] underline">
                    En savoir plus
                  </Link>
                  .
                </span>
              </label>

              <Button
                type="submit"
                data-testid="claim-submit"
                disabled={!isValid || submitting}
                className="w-full bg-[#0B3A2A] hover:bg-[#0B3A2A]/90 text-white"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi…</>
                ) : (
                  <>Envoyer ma demande de revendication <Send className="w-4 h-4 ml-2" /></>
                )}
              </Button>

              <p className="text-[10px] text-slate-400 text-center leading-relaxed">
                Vos données sont traitées conformément à notre politique de
                confidentialité. L'équipe CONFILEX vous contactera sous 48h à l'email
                professionnel indiqué pour finaliser la vérification.
              </p>
            </form>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function SuccessCard({ email, lawyer }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
      <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
        <Check className="w-7 h-7 text-green-700" />
      </div>
      <h2 className="font-serif text-2xl text-[#0B3A2A] mb-2">
        Votre demande a bien été enregistrée
      </h2>
      <p className="text-sm text-slate-600 mb-6 max-w-lg mx-auto">
        Un membre de l'équipe CONFILEX vous contactera à <strong>{email}</strong>{" "}
        {lawyer?.barreau && <>concernant la fiche rattachée au barreau de <strong>{lawyer.barreau}</strong>{" "}</>}
        sous 48 heures ouvrées pour finaliser la vérification et vous transmettre
        les accès de gestion.
      </p>
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600 mb-6 text-left max-w-lg mx-auto">
        <p className="font-medium text-slate-700 mb-2">Prochaines étapes :</p>
        <ol className="list-decimal pl-5 space-y-1">
          <li>Vérification de votre inscription à l'Ordre</li>
          <li>Envoi d'un lien sécurisé pour créer votre compte professionnel</li>
          <li>Rattachement de la fiche publique à votre compte</li>
          <li>Accès à l'espace de gestion (biographie, coordonnées, contenus)</li>
        </ol>
      </div>
      <Link
        to="/reseau-specialistes"
        className="inline-flex items-center gap-1.5 text-sm text-[#0B3A2A] font-medium hover:underline"
      >
        Retour à l'annuaire <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}
